import { Head, Link } from "@inertiajs/react"
import { Card } from "@/components/ui/card"
import { PageLayout } from "@/components/VocationalTest/PageLayout"
import { Shield, Mail, MapPin, ArrowLeft } from "lucide-react"
import { Button } from "@/components/ui/button"

export default function PrivacyPolicyPage() {
  return (
    <>
      <Head title="Política de Privacidade - Orientação Profissional" />
      <PageLayout>
        <Card className="max-w-4xl mx-auto p-8 md:p-12 bg-white">
          <div className="space-y-8">
            {/* Header */}
            <div className="space-y-4 text-center border-b pb-8">
              <div className="flex items-center justify-center gap-3">
                <Shield className="w-10 h-10 text-primary" />
                <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                  Política de Privacidade
                </h1>
              </div>
              <p className="text-lg text-muted-foreground">
                App Orientação Profissional com o 25Método S.E.M.E.A.R.
              </p>
              <p className="text-sm text-muted-foreground">
                Última atualização: Fevereiro de 2026
              </p>
            </div>

            {/* Section 1 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">1. Quem Somos</h2>
              <p className="text-muted-foreground leading-relaxed">
                A <strong>25SEGUNDOS DESENVOLVIMENTO E TREINAMENTO</strong> (CNPJ 30.977.264/0001-60) é a responsável pelo tratamento dos seus dados pessoais.
              </p>
              <div className="bg-accent/10 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span>Rua Curitiba, 160 – Bairro Nova Mogi – Mogi Mirim/SP – CEP 13800-310</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  <span>atendimento@25segundos.com.br</span>
                </div>
              </div>
            </section>

            {/* Section 2 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">2. Quais Dados Coletamos</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Dados de Cadastro</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                    <li>Nome completo</li>
                    <li>E-mail (com confirmação)</li>
                    <li>Idade</li>
                    <li>Escola</li>
                    <li>Série/ano escolar</li>
                    <li>Cidade e Estado</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Dados do Teste Vocacional</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                    <li>Respostas às 45 questões do Teste Básico</li>
                    <li>Respostas às 25 questões do Teste Avançado (se contratado)</li>
                    <li>Interesses profissionais identificados</li>
                    <li>Habilidades percebidas</li>
                    <li>Preferências de carreira</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Dados Sensíveis</h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                    <li>Perfil comportamental (baseado no Eneagrama)</li>
                    <li>Autopercepção emocional (nas respostas do teste)</li>
                  </ul>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 font-medium">
                  Importante: Coletamos apenas dados essenciais para oferecer a orientação profissional.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">3. Como Coletamos</h2>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li><strong>No cadastro:</strong> quando você cria sua conta</li>
                <li><strong>Nos testes:</strong> ao responder os questionários (você clica nas alternativas)</li>
                <li><strong>Em pagamentos:</strong> quando opta por produtos pagos</li>
              </ul>
              <p className="text-muted-foreground">
                Você usa o app apenas 1 vez para fazer o teste (não há acompanhamento contínuo).
              </p>
            </section>

            {/* Section 4 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">4. Para Que Usamos Seus Dados</h2>
              <div className="space-y-2">
                <p className="text-muted-foreground flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Criar e gerenciar sua conta</span>
                </p>
                <p className="text-muted-foreground flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Aplicar os testes de orientação profissional e definir seu perfil vocacional</span>
                </p>
                <p className="text-muted-foreground flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Gerar relatórios personalizados (Básico e Avançado)</span>
                </p>
                <p className="text-muted-foreground flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Enviar seus resultados por e-mail</span>
                </p>
                <p className="text-muted-foreground flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Informar sobre produtos complementares (livros, cursos, mentorias) — quando relevante</span>
                </p>
                <p className="text-muted-foreground flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Cumprir obrigações legais</span>
                </p>
              </div>
              <p className="text-muted-foreground flex items-start gap-2">
                <span className="text-red-600">✗</span>
                <span><strong>Não fazemos:</strong> acompanhamento contínuo, atualização de perfil ou uso para publicidade.</span>
              </p>
            </section>

            {/* Section 5 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">5. Compartilhamento de Dados</h2>
              <p className="text-muted-foreground font-semibold">
                Nunca vendemos seus dados pessoais.
              </p>
              <p className="text-muted-foreground">Compartilhamos apenas com:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                <li>Plataformas de hospedagem segura (para armazenar o app)</li>
                <li>Provedores de pagamento (se você comprar produtos pagos)</li>
                <li>Parceiros técnicos (para funcionamento do app)</li>
              </ul>
              <p className="text-muted-foreground">
                Todos seguem rigorosos padrões de segurança e só acessam o necessário.
              </p>
            </section>

            {/* Section 6 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">6. Segurança dos Dados</h2>
              <p className="text-muted-foreground font-semibold">Como protegemos:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                <li>Criptografia de senhas</li>
                <li>Acesso restrito apenas a funcionários autorizados</li>
                <li>Autenticação por confirmação de e-mail</li>
                <li>Plataformas com certificações de segurança</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">7. Por Quanto Tempo Guardamos</h2>
              <p className="text-muted-foreground">Seus dados são mantidos:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                <li>Durante o uso ativo da conta</li>
                <li>Pelo prazo legal obrigatório após encerramento</li>
                <li>Dados de menores: com cuidado reforçado conforme LGPD</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">8. Menores de Idade</h2>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                <li>O app é destinado a estudantes a partir de 14 anos</li>
                <li>Menores de 18 anos precisam de autorização dos pais/responsáveis</li>
                <li>A escola informa os pais sobre o app e sua importância</li>
                <li>Pais/responsáveis autorizam o uso antes do cadastro</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">9. Seus Direitos (LGPD)</h2>
              <p className="text-muted-foreground">Você pode:</p>
              <div className="space-y-2">
                <p className="text-muted-foreground flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span><strong>Confirmar</strong> se tratamos seus dados</span>
                </p>
                <p className="text-muted-foreground flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span><strong>Acessar</strong> seus dados</span>
                </p>
                <p className="text-muted-foreground flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span><strong>Corrigir</strong> informações incompletas ou erradas</span>
                </p>
                <p className="text-muted-foreground flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span><strong>Solicitar exclusão</strong> de dados (exceto quando houver obrigação legal de manter)</span>
                </p>
                <p className="text-muted-foreground flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span><strong>Revogar consentimento</strong> a qualquer momento</span>
                </p>
                <p className="text-muted-foreground flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span><strong>Solicitar portabilidade</strong> dos dados</span>
                </p>
              </div>
              <p className="text-muted-foreground">
                <strong>Como exercer:</strong> envie e-mail para <strong>atendimento@25segundos.com.br</strong>
              </p>
            </section>

            {/* Section 10 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">10. Cookies</h2>
              <p className="text-muted-foreground">
                Usamos apenas <strong>cookies essenciais</strong> para o funcionamento básico do app (exemplo: manter você logado).
              </p>
            </section>

            {/* Section 11 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">11. Atualizações desta Política</h2>
              <p className="text-muted-foreground">Podemos atualizar esta Política de Privacidade. Quando isso acontecer:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                <li>Você será notificado por e-mail</li>
                <li>A data da "Última atualização" será modificada</li>
                <li>Mudanças importantes serão destacadas</li>
              </ul>
            </section>

            {/* Section 12 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">12. Contato e Dúvidas</h2>
              <p className="text-muted-foreground">
                Para questões sobre privacidade, dados pessoais ou exercício de direitos:
              </p>
              <div className="bg-accent/10 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="w-4 h-4" />
                  <span>atendimento@25segundos.com.br</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span>Rua Curitiba, 160 – Bairro Nova Mogi – Mogi Mirim/SP – CEP 13800-310</span>
                </div>
              </div>
            </section>

            {/* Section 13 */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground">13. Lei Aplicável</h2>
              <p className="text-muted-foreground">
                Esta Política segue a <strong>Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018)</strong> e demais leis brasileiras aplicáveis.
              </p>
            </section>

            {/* Footer */}
            <div className="border-t pt-8 space-y-4">
              <p className="text-center text-muted-foreground font-medium">
                Ao usar o app, você declara ter lido e compreendido esta Política de Privacidade.
              </p>
              <p className="text-center text-muted-foreground">
                Se menor de 18 anos, confirme que seus pais/responsáveis autorizaram o uso.
              </p>
              <div className="flex justify-center pt-4">
                <Link href="/">
                  <Button variant="outline" className="gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    Voltar ao início
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Card>
      </PageLayout>
    </>
  )
}